import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { getUserFriendlyEventError } from '@/lib/crm/core/errors';
import { createEventBuilder } from '@/lib/crm/core/EventBuilder';
import { createEventsClient } from '@/lib/crm/core/EventsClient';
import { fetchFreshSeq } from '@/lib/crm/core/utils';
import { logger } from '@/lib/logger';

export interface EditingTeam {
  entity_id: string;
  display_name: string;
  description: string;
  last_event_seq: number;
}

interface UseTeamSaveParams {
  editingTeam: EditingTeam | null | undefined;
  displayName: string;
  description: string;
  userId: string;
  orgId: number;
  onOpenChange: (open: boolean) => void;
  onSaved: (() => void) | undefined;
  selectedUserIds: Set<string>;
  initialMemberUserIds: readonly string[];
}

export function useTeamSave({
  editingTeam,
  displayName,
  description,
  userId,
  orgId,
  onOpenChange,
  onSaved,
  selectedUserIds,
  initialMemberUserIds,
}: UseTeamSaveParams): {
  isSaving: boolean;
  handleSave: (setSubmitError: (msg: string) => void) => Promise<void>;
} {
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = useCallback(
    async (setSubmitError: (msg: string) => void) => {
      setIsSaving(true);
      try {
        if (editingTeam) {
          // EDIT mode — emit property changes + member changes.
          const trimmedName = displayName.trim();
          const trimmedDesc = description.trim();
          const changedProps: Record<string, unknown> = {};

          if (trimmedName !== editingTeam.display_name.trim()) {
            changedProps.ahoy_display_name = trimmedName;
          }
          if (trimmedDesc !== editingTeam.description.trim()) {
            changedProps.ahoy_description = trimmedDesc;
          }

          // Compute member changes
          const initialSet = new Set(initialMemberUserIds);
          const toAdd = [...selectedUserIds].filter(id => !initialSet.has(id));
          const toRemove = [...initialMemberUserIds].filter(
            id => !selectedUserIds.has(id)
          );

          const hasPropertyChanges = Object.keys(changedProps).length > 0;
          const hasMemberChanges = toAdd.length > 0 || toRemove.length > 0;

          if (!hasPropertyChanges && !hasMemberChanges) {
            // Nothing changed — close without submitting.
            onOpenChange(false);
            return;
          }

          // Re-fetch fresh seq to avoid stale-sequence conflicts.
          const startSeq = await fetchFreshSeq(
            orgId,
            'ahoy_team',
            editingTeam.entity_id
          );

          const builder = createEventBuilder({
            sourceType: 'ahoy_user',
            sourceId: userId,
            startSeq,
          });

          if (hasPropertyChanges) {
            builder.propertiesUpdated(
              editingTeam.entity_id,
              'ahoy_team',
              changedProps
            );
          }

          for (const id of toAdd) {
            builder.associationAdded(
              editingTeam.entity_id,
              'ahoy_team',
              'ahoy_team_to_user',
              id
            );
          }
          for (const id of toRemove) {
            builder.associationRemoved(
              editingTeam.entity_id,
              'ahoy_team',
              'ahoy_team_to_user',
              id
            );
          }

          await createEventsClient(orgId).submitEvents(builder.build());
        } else {
          // CREATE mode — generate a fresh UUID for the new entity.
          const teamId = crypto.randomUUID();
          const builder = createEventBuilder({
            sourceType: 'ahoy_user',
            sourceId: userId,
          });
          const trimmedName = displayName.trim();
          const trimmedDesc = description.trim();
          builder.objectCreated(teamId, 'ahoy_team', {
            ahoy_display_name: trimmedName,
            ...(trimmedDesc ? { ahoy_description: trimmedDesc } : {}),
          });

          // Add member associations for the new team
          for (const id of selectedUserIds) {
            builder.associationAdded(
              teamId,
              'ahoy_team',
              'ahoy_team_to_user',
              id
            );
          }

          await createEventsClient(orgId).submitEvents(builder.build());
        }

        toast.success(editingTeam ? 'Team updated' : 'Team created');
        onSaved?.();
        onOpenChange(false);
      } catch (err) {
        const message = getUserFriendlyEventError(err);
        toast.error(message);
        setSubmitError(message);
        logger.error(
          'Failed to save team',
          err instanceof Error ? err : new Error(String(err)),
          { orgId, entity_id: editingTeam?.entity_id }
        );
      } finally {
        setIsSaving(false);
      }
    },
    [
      editingTeam,
      displayName,
      description,
      userId,
      orgId,
      onOpenChange,
      onSaved,
      selectedUserIds,
      initialMemberUserIds,
    ]
  );

  return { isSaving, handleSave };
}
